import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Vote, 
  Check,
  Loader2,
  Mail,
  Play,
  BarChart3,
  Image,
  ExternalLink,
  Shield
} from "lucide-react";
import { toast } from "sonner";
import { publicAPI } from "../lib/api";
import AnimatedFooter from "../components/AnimatedFooter";

export default function PublicPoll() {
  const { id } = useParams();
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [rgpdConsent, setRgpdConsent] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm();

  useEffect(() => {
    loadPoll();
    const interval = setInterval(loadPoll, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, [id]);

  const loadPoll = async () => {
    try {
      const res = await publicAPI.getPoll(id);
      setPoll(res.data);
    } catch (error) {
      toast.error("Sondage non trouvé");
    } finally {
      setLoading(false);
    }
  };

  const onVote = async (data) => {
    if (!selectedOption) {
      toast.error("Sélectionnez une option");
      return;
    }
    
    setVoting(true);
    try {
      await publicAPI.vote(id, {
        option_id: selectedOption,
        voter_email: data.email,
        rgpd_consent: rgpdConsent
      });
      toast.success("Vote enregistré !");
      setHasVoted(true);
      setRgpdConsent(false);
      loadPoll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du vote");
    } finally {
      setVoting(false);
    }
  };

  const totalVotes = poll?.total_votes || 0;
  const getVotePercentage = (votes) => totalVotes > 0 ? ((votes / totalVotes) * 100).toFixed(1) : 0;

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!poll) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <p className="text-white text-xl">Sondage non trouvé</p>
      </div>
    );
  }

  const isActive = poll.status === "active";
  const isClosed = poll.status === "closed";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      {/* Header */}
      <header className="p-6 flex items-center justify-between max-w-5xl mx-auto">
        <div className="flex items-center gap-4">
          {poll.organizer_logo ? (
            <img src={poll.organizer_logo} alt="Logo" className="h-12 w-auto rounded-lg" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
              <Vote className="w-6 h-6 text-white" />
            </div>
          )}
          <div>
            <h1 className="text-white font-bold text-xl" style={{ fontFamily: 'Outfit' }}>
              {poll.organizer_name}
            </h1>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {isClosed ? (
            <Badge className="bg-slate-500 text-white">Fermé</Badge>
          ) : isActive ? (
            <Badge className="bg-green-500 text-white live-indicator">En cours</Badge>
          ) : (
            <Badge className="bg-slate-500 text-white">Brouillon</Badge>
          )}
          <Badge variant="outline" className="text-white border-white/30">
            <BarChart3 className="w-4 h-4 mr-2" />
            {totalVotes} votes
          </Badge>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
            {poll.title}
          </h2>
          {poll.description && (
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              {poll.description}
            </p>
          )}
        </motion.div>

        {/* Voting or Results */}
        {(hasVoted || isClosed) ? (
          // Results View
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white text-center mb-8">
              <BarChart3 className="w-6 h-6 inline-block mr-2" />
              Résultats
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              {poll.options
                ?.sort((a, b) => (b.votes || 0) - (a.votes || 0))
                .map((option, index) => (
                  <motion.div
                    key={option.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className={`bg-white/5 border-white/10 overflow-hidden ${index === 0 && totalVotes > 0 ? 'ring-2 ring-amber-500' : ''}`}>
                      {option.image_url && (
                        <div className="h-40 overflow-hidden">
                          <img 
                            src={option.image_url} 
                            alt={option.text || option.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              {index === 0 && totalVotes > 0 && (
                                <Badge className="bg-amber-500 text-white">1er</Badge>
                              )}
                              <h4 className="text-lg font-semibold text-white">{option.text || option.title}</h4>
                            </div>
                            {option.description && (
                              <p className="text-white/60 text-sm">{option.description}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-white">{getVotePercentage(option.votes || 0)}%</p>
                            <p className="text-white/60 text-sm">{option.votes || 0} votes</p>
                          </div>
                        </div>
                        <Progress 
                          value={getVotePercentage(option.votes || 0)} 
                          className={`h-3 ${index === 0 && totalVotes > 0 ? '[&>div]:bg-amber-500' : '[&>div]:bg-primary'}`}
                        />
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
            </div>
          </div>
        ) : isActive ? (
          // Voting View
          <form onSubmit={handleSubmit(onVote)} className="space-y-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {poll.options?.map((option, index) => {
                const embedUrl = getYouTubeEmbedUrl(option.video_url);
                
                return (
                  <motion.div
                    key={option.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card 
                      className={`bg-white/5 border-2 cursor-pointer transition-all hover:bg-white/10 ${
                        selectedOption === option.id 
                          ? 'border-primary ring-2 ring-primary/50' 
                          : 'border-white/10'
                      }`}
                      onClick={() => setSelectedOption(option.id)}
                      data-testid={`vote-option-${option.id}`}
                    >
                      {option.image_url && (
                        <div className="h-40 overflow-hidden rounded-t-lg">
                          <img 
                            src={option.image_url} 
                            alt={option.text || option.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      
                      {embedUrl && (
                        <div className="aspect-video">
                          <iframe
                            src={embedUrl}
                            title={option.text || option.title}
                            className="w-full h-full"
                            allowFullScreen
                          />
                        </div>
                      )}
                      
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-lg font-semibold text-white mb-1">{option.text || option.title}</h4>
                            {option.description && (
                              <p className="text-white/60 text-sm">{option.description}</p>
                            )}
                          </div>
                          {selectedOption === option.id && (
                            <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            {/* Email Input & Submit */}
            <Card className="bg-white/10 border-white/20 max-w-md mx-auto">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-3.5 text-white/50" />
                    <Input
                      type="email"
                      placeholder="Votre email pour voter *"
                      className="h-11 bg-white/5 border-white/10 text-white placeholder:text-white/40 pl-10 focus:border-white/30"
                      data-testid="voter-email-input"
                      {...register("email", { 
                        required: "Email requis",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Email invalide"
                        }
                      })}
                    />
                    {errors.email && (
                      <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
                    )}
                  </div>
                  
                  {/* RGPD Consent */}
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="rgpd-poll-consent"
                        checked={rgpdConsent}
                        onCheckedChange={setRgpdConsent}
                        className="mt-0.5 border-white/30 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                      />
                      <label htmlFor="rgpd-poll-consent" className="text-sm text-white/70 cursor-pointer leading-relaxed">
                        J'accepte de recevoir des communications de l'organisateur
                        <span className="text-white/50"> (Optionnel)</span>
                      </label>
                    </div>
                    <p className="text-xs text-white/40 flex items-start gap-2 mt-2">
                      <Shield className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>
                        Vos données sont protégées. Contact : <a href="mailto:rgpd@tireo.app" className="text-white/60 underline">rgpd@tireo.app</a>
                      </span>
                    </p>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold"
                    disabled={voting || !selectedOption}
                    data-testid="submit-vote-btn"
                  >
                    {voting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Vote className="w-5 h-5 mr-2" />
                        Voter
                      </>
                    )}
                  </Button>
                  
                  <p className="text-white/50 text-xs text-center">
                    {poll.max_votes_per_email > 1 
                      ? `${poll.max_votes_per_email} votes maximum par email`
                      : "1 vote par email"
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
          </form>
        ) : (
          // Draft View
          <div className="text-center py-12">
            <Vote className="w-16 h-16 text-white/30 mx-auto mb-4" />
            <p className="text-white/60 text-lg">
              Ce sondage n'est pas encore actif.
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <AnimatedFooter variant="transparent" />
    </div>
  );
}
